/**
 * The pane's server API — the one place that talks to `/outlook/*`. Every endpoint
 * is a named function; nothing outside this file constructs a request or knows a
 * URL. Callers get back a normalized `{ ok, status, data, error }` and branch on it.
 *
 * `apiFetch` (private) is the shared transport: it attaches the pane's access token
 * (the fts session_oid) as an RFC 6750 Bearer, runs the shared 401 guard
 * (`check401`, which clears state + redirects to Connect and throws
 * SessionExpiredError so the caller stops), and parses the JSON body. `json` sends
 * a JSON body; `body` sends raw bytes (chunk uploads). A non-JSON/empty response
 * yields `{}`, so `data`/`error` are simply undefined. A 401 is never swallowed.
 *
 * Not here: NAA/auth (js/utils/auth.js) — that's the pre-session bootstrap; it uses
 * a Microsoft token (not the session_oid) and must not run through the expiry guard.
 */

import { getSessionId } from "./roaming_settings.js";
import { check401 } from "./session.js";

const enc = encodeURIComponent;

async function apiFetch(path, { method = "GET", json, body, headers } = {}) {
  const init = { method, headers: { Authorization: `Bearer ${getSessionId()}`, ...(headers || {}) } };
  if (json !== undefined) {
    init.headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(json);
  } else if (body !== undefined) {
    init.body = body;
  }
  const resp = await fetch(path, init);
  check401(resp); // throws SessionExpiredError on 401 (redirect to Connect in flight)
  const payload = await resp.json().catch(() => ({}));
  return { ok: resp.ok, status: resp.status, data: payload.data, error: payload.error };
}

// --- Transfer resource: /outlook/send + /outlook/transfer/* -----------------

/** Create + send the transfer from staged uploads. Returns per-file content oids. */
export function sendTransfer({ subject, recipients, fileOids }) {
  return apiFetch("/outlook/send", { method: "POST", json: { subject, recipients, file_oids: fileOids } });
}

/** Read a sent transfer's current state (resume on reopen); 404 if deleted/not owned. */
export function getTransferMetadata(oid) {
  return apiFetch(`/outlook/transfer/${enc(oid)}`);
}

/** Extend a resumed transfer's expiration back to a full delivery window (extend-only). */
export function renewTransfer(oid) {
  return apiFetch(`/outlook/transfer/${enc(oid)}/renew`, { method: "POST" });
}

/** Sync the recipient-gated transfer's receivers to the email's current recipients. */
export function syncTransferRecipients(oid, recipients) {
  return apiFetch(`/outlook/transfer/${enc(oid)}/recipients`, { method: "POST", json: { recipients } });
}

/** Attach a freshly-uploaded file to an existing transfer; returns its content oid. */
export function addTransferFile(oid, uploadOid) {
  return apiFetch(`/outlook/transfer/${enc(oid)}/files`, { method: "POST", json: { upload_oid: uploadOid } });
}

/** Remove a file from an existing transfer (server refuses to drop the last one). */
export function removeTransferFile(oid, contentOid) {
  return apiFetch(`/outlook/transfer/${enc(oid)}/files/${enc(contentOid)}/delete`, { method: "POST" });
}

/** Delete a transfer the pane no longer references (best-effort at the call site). */
export function deleteTransfer(oid) {
  return apiFetch(`/outlook/transfer/${enc(oid)}/delete`, { method: "POST" });
}

// --- Staged-upload resource: /outlook/files/* -------------------------------

/** Register a file for upload; `data.oid` is the staged-upload id. */
export function registerUpload(file) {
  return apiFetch("/outlook/files/register", {
    method: "POST",
    json: { file_name: file.name, file_size: file.size },
  });
}

/** Ask for the next chunk descriptor: `data` is { chunk_id, start?, end? }. */
export function initChunk(oid) {
  return apiFetch(`/outlook/files/${enc(oid)}/init`, { method: "POST" });
}

/** Upload one chunk's raw bytes; the server relays it to S3 before resolving. */
export function uploadChunk(oid, chunkId, bytes) {
  return apiFetch(`/outlook/files/${enc(oid)}/chunk/${enc(chunkId)}`, { method: "POST", body: bytes });
}

/** Finalize a fully-chunked upload. */
export function confirmUpload(oid) {
  return apiFetch(`/outlook/files/${enc(oid)}/confirm`, { method: "POST" });
}

/** Delete a staged (not-yet-sent) upload. */
export function deleteUpload(oid) {
  return apiFetch(`/outlook/files/${enc(oid)}/delete`, { method: "POST" });
}

// --- Auth ------------------------------------------------------------------

/** End the LeapFILE session + unlink the Microsoft account server-side. */
export function signout() {
  return apiFetch("/outlook/auth/signout", { method: "POST" });
}
